import { api } from './api';

// Types
export interface User {
    id: string;
    email: string;
    name?: string;
    avatarUrl?: string;
    emailVerified: boolean;
    createdAt: string;
}

export interface AuthResponse {
    user: User;
    token: string;
    expiresIn: string;
}

export interface RegisterRequest {
    email: string;
    password: string;
    name?: string;
}

export interface LoginRequest {
    email: string;
    password: string;
}

export interface UserSettings {
    defaultTheme?: string;
    notifications?: {
        enabled: boolean;
        reminderMinutes?: number;
    };
    preferences?: {
        language?: string;
        timezone?: string;
    };
}

// Token storage keys
const TOKEN_KEY = 'lunar_calendar_token';
const USER_KEY = 'lunar_calendar_user';

// Token management
export const tokenStorage = {
    getToken: (): string | null => {
        return localStorage.getItem(TOKEN_KEY);
    },
    setToken: (token: string): void => {
        localStorage.setItem(TOKEN_KEY, token);
    },
    removeToken: (): void => {
        localStorage.removeItem(TOKEN_KEY);
    },
    getUser: (): User | null => {
        const data = localStorage.getItem(USER_KEY);
        return data ? JSON.parse(data) : null;
    },
    setUser: (user: User): void => {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    },
    removeUser: (): void => {
        localStorage.removeItem(USER_KEY);
    },
    clear: (): void => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
    }
};

// Add auth header interceptor
api.interceptors.request.use((config) => {
    const token = tokenStorage.getToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Auth API functions
export const authService = {
    async register(data: RegisterRequest): Promise<AuthResponse> {
        const response = await api.post<AuthResponse>('/auth/register', data);
        const { user, token } = response.data;
        tokenStorage.setToken(token);
        tokenStorage.setUser(user);
        return response.data;
    },

    async login(data: LoginRequest): Promise<AuthResponse> {
        const response = await api.post<AuthResponse>('/auth/login', data);
        const { user, token } = response.data;
        tokenStorage.setToken(token);
        tokenStorage.setUser(user);
        return response.data;
    },

    async logout(): Promise<void> {
        try {
            await api.post('/auth/logout');
        } catch (error) {
            // Ignore errors, clear tokens anyway
        }
        tokenStorage.clear();
    },

    async refreshToken(): Promise<string> {
        const response = await api.post<{ token: string; expiresIn: string }>('/auth/refresh');
        tokenStorage.setToken(response.data.token);
        return response.data.token;
    },

    async getProfile(): Promise<User> {
        const response = await api.get<User>('/user/me');
        tokenStorage.setUser(response.data);
        return response.data;
    },

    async getSettings(): Promise<UserSettings> {
        const response = await api.get<UserSettings>('/user/settings');
        return response.data;
    },

    async updateSettings(settings: Partial<UserSettings>): Promise<UserSettings> {
        const response = await api.put<UserSettings>('/user/settings', settings);
        return response.data;
    },

    isAuthenticated(): boolean {
        return !!tokenStorage.getToken();
    },

    getCurrentUser(): User | null {
        return tokenStorage.getUser();
    }
};

export default authService;
